<template>
    <div class="order-info-header">
        <div class="summary-row">
            <div class="order-info">
                <div class="number">{{ orderNumber }}</div>

                <div v-if="hasOpenBill" class="table with-open-bill" @click="openBillTableClick">
                    {{ order.customTableName }}
                </div>
                <div v-if="!hasOpenBill" class="table">{{ order.customTableName }}</div>
            </div>
            <div class="order-time-info">
                <div class="time">{{ orderTime }}</div>
                <div class="date">{{ orderDate }}</div>
                <div
                    v-if="fulfillmentReadyDateTime"
                    class="fulfilment-ready-date"
                    @click="onFulfilmentReadyDateTimeClick"
                    :class="{ clickable: hasOrderDeliveryAddress }"
                >
                    <i class="fa-light fa-person-carry-box"></i>{{ fulfillmentReadyDateTime }}
                </div>
            </div>
        </div>
        <div class="waiter-and-statuses-row">
            <div class="waiter">
                {{ ordererUserName }}
                <i
                    v-if="hasOrdererContactDetails"
                    class="contact-details-icon fa-light fa-comment"
                    @click="toggleContactDetails"
                >
                </i>

                <ContactDetailsDialog
                    ref="op"
                    v-if="hasOrdererContactDetails"
                    :email="order.indirectIdentityEmail"
                    :phoneNumber="order.indirectIdentityPhoneNumber"
                />
            </div>
            <div class="statuses-row">
                <div class="use-case">
                    <span v-if="orderApplicationName">{{ orderApplicationName }}</span>
                </div>
                <div class="order-states">
                    <OrderStateBadge :orderState="order.state" />
                    <CancellationStateBadge
                        v-if="order.cancellationState"
                        :cancellationState="order.cancellationState"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { IFullOrder } from "@/data/model/Order";
    import { DateTime } from "@/data/util/DateTime";
    import { Format } from "@/data/util/Format";
    import { OrderHelper } from "@/data/util/OrderHelper";
    import { OperatorClient } from "@/operator/OperatorClient";
    import { operatorEvents } from "@/operator/events/operatorEvents";
    import { trans } from "@/operator/localization/Trans";
    import { computed, ref } from "vue";

    import CancellationStateBadge from "@/operator/components/order/CancellationStateBadge.vue";
    import ContactDetailsDialog from "@/operator/components/order/ContactDetailsDialog.vue";
    import OrderStateBadge from "@/operator/components/order/OrderStateBadge.vue";

    const props = defineProps<{
        order: IFullOrder;
    }>();

    const orderNumber = computed(() => {
        return props.order.dailyOrderIncrement !== undefined
            ? OrderHelper.padDailyIncrement(props.order.dailyOrderIncrement)
            : props.order.orderNumber;
    });

    const orderApplicationName = computed(() =>
        Format.translate(props.order.useCaseName, OperatorClient.context.settings!.location!.language)
    );

    const orderTime = computed(() => DateTime.toTimeString(props.order.confirmedDateTime ?? props.order.created, true));
    const orderDate = computed(() => DateTime.toDateString(props.order.confirmedDateTime ?? props.order.created));
    const hasOrderDeliveryAddress = computed(() => props.order.deliveryAddress != null);

    const fulfillmentReadyDateTime = computed(() => {
        if (props.order.effectiveFulfillmentDateTime !== undefined) {
            return DateTime.toPreciseDateTimeString(props.order.effectiveFulfillmentDateTime);
        } else if (props.order.effectiveReadyDateTime !== undefined) {
            return DateTime.toPreciseDateTimeString(props.order.effectiveReadyDateTime);
        } else if (props.order.scheduledFulfillmentDateTime !== undefined) {
            return DateTime.toPreciseDateTimeString(props.order.scheduledFulfillmentDateTime);
        } else return undefined;
    });

    const hasOpenBill = computed(() => {
        return props.order.isBillOpen && props.order.billId != null && props.order.tableId != null;
    });

    const ordererUserName = computed(() => {
        let userName = props.order.userName;
        if (userName === "Anonymer User") {
            return trans.orders.anonymousUser;
        }

        if (userName != null) {
            return userName;
        }

        return null;
    });

    const hasOrdererContactDetails = computed(() => {
        return props.order.indirectIdentityEmail || props.order.indirectIdentityPhoneNumber;
    });

    function openBillTableClick() {
        operatorEvents.orderDetailsModal.openBillTableSelected.dispatch({
            tableId: props.order.tableId!,
            billId: props.order.billId!,
        });
    }

    function onFulfilmentReadyDateTimeClick() {
        if (hasOrderDeliveryAddress.value) {
            operatorEvents.orderDetailsModal.showDeliveryAddressClicked.dispatch(props.order.id);
        }
    }

    const op = ref();
    function toggleContactDetails(event) {
        op.value.toggle(event);
    }
</script>

<style lang="scss">
    @use "./../../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../../assets/styles/scss/shared/_mixins.scss";
    @use "./../../../../assets/styles/scss/shared/_variables.scss";

    .order-info-header {
        > .summary-row {
            display: flex;
            justify-content: space-between;

            > .order-info {
                > .number {
                    font-size: 18px;
                    line-height: 22px;
                    font-weight: 600;
                    text-transform: uppercase;
                    padding-bottom: 3px;
                }

                > .table {
                    font-size: 16px;
                    line-height: 16px;
                    color: #aaaaaa;
                    font-weight: 600;
                    position: relative;

                    &.with-open-bill {
                        cursor: pointer;
                        color: colors.$green;
                        transition: color 0.5s variables.$easeOutExpo;

                        @include mixins.hover-and-active() {
                            color: colors.$greenHover;
                        }

                        &:after {
                            content: "";
                            position: absolute;
                            top: 0;
                            right: -4px;
                            bottom: 0;
                            left: -4px;
                        }
                    }
                }
            }

            > .order-time-info {
                text-align: right;

                > .time {
                    font-size: 16px;
                    line-height: 20px;
                    font-weight: 600;
                    padding-bottom: 2px;
                }

                > .date {
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 300;
                    color: #aaaaaa;
                }

                > .fulfilment-ready-date {
                    padding-top: 4px;
                    font-size: 14px;
                    line-height: 16px;
                    font-weight: 600;
                    color: #aaaaaa;

                    &.clickable {
                        cursor: pointer;
                        transition: color 0.5s variables.$easeOutExpo;

                        @include mixins.hover-and-active() {
                            color: colors.$fontColorHover;
                        }
                    }

                    > .fa-person-carry-box {
                        margin-right: 4px;
                        background-color: colors.$green;
                        color: white;
                        padding: 4px;
                        border-radius: 2px;
                    }
                }
            }
        }

        > .waiter-and-statuses-row {
            margin-top: 9px;
            display: flex;
            justify-content: space-between;

            > .waiter {
                font-weight: bold;

                > .contact-details-icon {
                    margin-left: 0.2em;
                    font-size: 1.25em;
                    color: #484848;
                    cursor: pointer;
                    position: relative;

                    &:after {
                        content: "";
                        position: absolute;
                        top: -4px;
                        right: -4px;
                        bottom: -4px;
                        left: -4px;
                    }
                }
            }

            > .statuses-row {
                display: flex;

                > .use-case {
                    span {
                        height: 22px;
                        display: inline-block;
                        line-height: 22px;
                        background-color: #484848;
                        color: #fff;
                        font-size: 12px;
                        font-weight: 400;
                        padding: 0 7px;
                        border-radius: 2px;
                    }
                }

                > .order-states {
                    span {
                        height: 22px;
                        line-height: 22px;
                        color: #fff;
                        font-size: 12px;
                        font-weight: 400;
                        white-space: nowrap;
                    }

                    .order-state-badge,
                    .cancellation-state-badge {
                        padding: 0 7px;
                        border-radius: 2px;
                        margin-left: 3px;
                        display: inline-block;
                    }
                }
            }
        }
    }
</style>
